import {
    Text,
    VStack,
    ModalFooter,
    Button,
    FormControl,
    FormLabel,
} from '@chakra-ui/react';
import {
    Controller, useFieldArray, useForm,
} from 'react-hook-form';
import { useState } from 'react';
import { Alert, RestrictedLengthTextAreaField } from '@acp/core-ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { ServiceShortName } from '@acp/shared';
import { getFileTypeFromPath, mapMimeTypeToMediaFileType } from '../../../../utils/file-util';
import { ReportedFindingsMachineContext } from './machine/context';
import { GenericUploadQueueProvider } from '../../../document/components/generic-uploader/context';
import { GenericDocumentUploadDropzone } from '../../../document/components/generic-uploader/dropzone';
import { EditThirdPartyResolutionFormSchema, thirdPartyResolutionFormSchema } from './forms/form-schema';

function ResolvedByThirdPartyDocumentForm({
    isSubmitting,
}: {
    isSubmitting?: boolean;
}) {
    const [isLoading, setState] = useState(false);
    const machine = ReportedFindingsMachineContext.useActorRef();

    const isMinimumStandards = ReportedFindingsMachineContext.useSelector(
        (state) => state.context.serviceShortName as ServiceShortName
            === ServiceShortName.MinimumStandards
    );

    const thirdPartyResolutionForm = ReportedFindingsMachineContext.useSelector(
        (state) => state.context.thirdPartyResolutionForm
    );

    const selectedProblemRegisterIds = ReportedFindingsMachineContext.useSelector(
        (state) => state.context.thirdPartyResolutionForm.selectedProblemRegisterIds
    );

    const reportedFindings = ReportedFindingsMachineContext.useSelector(
        (state) => state.context.reportedFindings
    );

    const selectedServicingProblemTitles = reportedFindings.filter(
        (finding) => selectedProblemRegisterIds.includes(
            finding.problemRegister.problemRegisterId
        )
    ).map((finding) => finding.servicingProblem.problemTitle);

    const IS_VIDEO_DOCUMENTATION_REQUIRED = selectedServicingProblemTitles.some((title) => [
        'Gas Stovetop - Faulty Ignition System',
        'Gas Stovetop Not Tested',
    ].includes(title));

    // useForm
    const {
        handleSubmit,
        getValues,
        control,
        formState,
    } = useForm<EditThirdPartyResolutionFormSchema>({
        resolver: zodResolver(thirdPartyResolutionFormSchema(isMinimumStandards)),
        defaultValues: {
            isVideoRequired: IS_VIDEO_DOCUMENTATION_REQUIRED,
            selectedProblemRegisterIds: [],
            notes: thirdPartyResolutionForm.notes,
            documents: thirdPartyResolutionForm.documents.map((d) => ({ ...d, isValid: true })),
        },
        mode: 'onChange',
    });

    const documentsFieldArray = useFieldArray({
        control,
        name: 'documents',
    });

    return (
        <VStack align="left" w="100%" gap={0} mb={4}>
            <VStack w="100%" alignItems="stretch" gap={6}>
                <Text fontSize="md" fontWeight="medium">
                    {isMinimumStandards ? 'Submit for re-assessment' : 'Mark as resolved by third-party'}
                </Text>
                <VStack align="start" spacing={2}>
                    <Text fontWeight="medium">
                        {selectedProblemRegisterIds.length}
                        {' of '}
                        {reportedFindings.length}
                        {` findings selected to be ${isMinimumStandards ? 'submitted for re-assessment' : 'marked as resolved'}.`}
                    </Text>
                    {reportedFindings
                        ?.filter((finding) => selectedProblemRegisterIds.includes(
                            finding.problemRegister.problemRegisterId
                        ))
                        .map((finding) => (
                            <Text key={finding.problemRegister.problemRegisterId}>
                                {finding.originalProblemItem.title}
                            </Text>
                        ))}
                </VStack>

                <FormControl
                    isRequired
                    isInvalid={!!formState.errors.documents}
                    mb={2}
                >
                    <FormLabel fontSize="md" fontWeight="bold">
                        Upload related documentation:
                    </FormLabel>

                    {IS_VIDEO_DOCUMENTATION_REQUIRED && (
                        <Alert
                            title="Please include a video demonstrating that the problem has been resolved."
                            status="info"
                            mb={4}
                        />
                    )}

                    <GenericUploadQueueProvider
                        uploadDestination="/v1/reported-findings/temporary-document"
                        onDocumentUploaded={({
                            fileReference,
                            name,
                            size,
                        }) => {
                            const mimeType = getFileTypeFromPath(
                                name
                            );
                            const fileType = mapMimeTypeToMediaFileType(mimeType);

                            documentsFieldArray.append({
                                id: fileReference,
                                name,
                                size,
                                type: fileType,
                                isValid: true,
                            });
                        }}
                        onFailedUploadCleared={(id) => {
                            const documentIdx = getValues('documents')
                                .findIndex((x) => x.id === id);

                            if (documentIdx === -1) return;

                            documentsFieldArray.remove(documentIdx);
                        }}
                        onUploadFailed={(params) => {
                            documentsFieldArray.append({
                                id: params.fileReference,
                                name: params.name,
                                size: params.size,
                                type: 'unknown',
                                isValid: false,
                            });
                        }}
                        onDocumentCleared={(fileReference) => {
                            const documentIdx = getValues('documents')
                                .findIndex((x) => x.id === fileReference);

                            if (documentIdx === -1) return;

                            documentsFieldArray.remove(documentIdx);
                        }}
                        defaultFiles={thirdPartyResolutionForm.documents.map((x) => ({
                            fileReference: x.id,
                            name: x.name,
                            size: x.size,
                        }))}
                        onIsUploadingChanged={(isUploading) => {
                            if (isUploading) {
                                setState(true);
                            } else {
                                setState(false);
                            }
                        }}
                    >
                        <GenericDocumentUploadDropzone />
                        {formState.errors.documents && (
                            <Text color="red.500" fontSize="sm">
                                {formState.errors.documents.message}
                            </Text>
                        )}
                    </GenericUploadQueueProvider>
                </FormControl>
                <Controller
                    control={control}
                    name="notes"
                    render={({ fieldState }) => (
                        <FormControl
                            isInvalid={fieldState.invalid}
                            mb={2}
                            isRequired={isMinimumStandards}
                        >
                            <RestrictedLengthTextAreaField
                                control={control}
                                name="notes"
                                label="Brief description of resolution"
                                maxLength={500}
                                isRequired={isMinimumStandards}
                            />
                        </FormControl>
                    )}
                />
            </VStack>
            <ModalFooter justifyContent="space-between">
                <Button
                    colorScheme="muted"
                    variant="ghost"
                    onClick={() => { machine.send({ type: 'BACK' }); }}
                >
                    Back
                </Button>
                <Button
                    colorScheme="secondary"
                    variant="solid"
                    isLoading={isLoading || isSubmitting === true}
                    isDisabled={!formState.isValid}
                    onClick={handleSubmit((vals) => {
                        machine.send({
                            type: 'SUBMIT_THIRD_PARTY_RESOLUTION',
                            thirdPartyResolutionForm: {
                                selectedProblemRegisterIds,
                                notes: vals.notes ?? '',
                                documents: vals.documents,
                            },
                        });
                    })}
                >
                    Next
                </Button>
            </ModalFooter>
        </VStack>
    );
}

export { ResolvedByThirdPartyDocumentForm };
